import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bookmark,
  BookmarkCheck,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Clock3,
  Copy,
  FileText,
  Filter,
  HardHat,
  Menu,
  Plus,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  X
} from "lucide-react";
import { promptLibrary, type ConstructionPrompt, type PromptCategory, type PromptRole, type PromptVariable } from "./prompt-data";

type LibraryView = "all" | "featured" | "favorites" | "recent";
type SortMode = "recommended" | "title" | "quickest";

const categoryIcons: Partial<Record<PromptCategory, typeof HardHat>> = {
  Executive: BriefcaseBusiness,
  Preconstruction: FileText,
  "Project Management": Clipboard,
  "Field Operations": HardHat,
  "Commercial/Contracts": ShieldCheck,
  "Safety/Quality": ShieldCheck,
  "Commercial / Contracts": ShieldCheck,
  "Safety / Quality": ShieldCheck
};

const categories = Array.from(new Set(promptLibrary.map((prompt) => prompt.category)));
const roles = Array.from(new Set(promptLibrary.map((prompt) => prompt.role)));

function readStoredList(key: string) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "[]") as string[];
  } catch {
    return [];
  }
}

export function PromptLibraryApp() {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<LibraryView>("all");
  const [category, setCategory] = useState<PromptCategory | "All">("All");
  const [role, setRole] = useState<PromptRole | "All">("All");
  const [sort, setSort] = useState<SortMode>("recommended");
  const [selected, setSelected] = useState<ConstructionPrompt | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => readStoredList("parallax-favorites"));
  const [recent, setRecent] = useState<string[]>(() => readStoredList("parallax-recent"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => localStorage.setItem("parallax-favorites", JSON.stringify(favorites)), [favorites]);
  useEffect(() => localStorage.setItem("parallax-recent", JSON.stringify(recent)), [recent]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === "Escape") setSelected(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const result = promptLibrary.filter((prompt) => {
      const matchesQuery = !normalized || [prompt.title, prompt.outcome, prompt.category, prompt.role, ...prompt.tags]
        .join(" ").toLowerCase().includes(normalized);
      const matchesCategory = category === "All" || prompt.category === category;
      const matchesRole = role === "All" || prompt.role === role;
      const matchesView = view === "all" || (view === "featured" && prompt.featured) ||
        (view === "favorites" && favorites.includes(prompt.id)) || (view === "recent" && recent.includes(prompt.id));
      return matchesQuery && matchesCategory && matchesRole && matchesView;
    });
    return [...result].sort((a, b) => {
      if (sort === "title") return a.title.localeCompare(b.title);
      if (sort === "quickest") return a.estimatedMinutes - b.estimatedMinutes;
      return Number(b.featured) - Number(a.featured);
    });
  }, [category, favorites, query, recent, role, sort, view]);

  function openPrompt(prompt: ConstructionPrompt) {
    setSelected(prompt);
    setRecent((current) => [prompt.id, ...current.filter((id) => id !== prompt.id)].slice(0, 8));
  }

  function toggleFavorite(id: string) {
    setFavorites((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function clearFilters() {
    setQuery("");
    setCategory("All");
    setRole("All");
    setView("all");
  }

  return (
    <div className="library-app">
      <header className="app-header">
        <button aria-label="Open navigation" className="mobile-menu" onClick={() => setMobileNavOpen(true)} type="button"><Menu size={20} /></button>
        <button className="library-brand" onClick={clearFilters} type="button"><img alt="PARALLAX" src="/brand/logo-transparent.png" /><span>Prompt Library</span></button>
        <label className="global-search"><Search size={18} /><span className="sr-only">Search prompts</span><input ref={searchRef} onChange={(event) => setQuery(event.target.value)} placeholder="Search RFIs, schedules, risk, contracts..." value={query} /><kbd>Ctrl K</kbd>{query && <button aria-label="Clear search" onClick={() => setQuery("")} type="button"><X size={16} /></button>}</label>
        <div className="header-actions"><span className="library-count"><strong>{promptLibrary.length}</strong> field-ready prompts</span><button className="new-prompt" onClick={() => searchRef.current?.focus()} type="button"><Plus size={16} /> Find a prompt</button></div>
      </header>

      <aside className={`library-sidebar ${mobileNavOpen ? "open" : ""}`}>
        <div className="sidebar-mobile-head"><strong>Browse library</strong><button aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} type="button"><X size={19} /></button></div>
        <nav aria-label="Library views" className="primary-nav">
          <NavButton active={view === "all"} icon={FileText} label="All prompts" onClick={() => { setView("all"); setMobileNavOpen(false); }} />
          <NavButton active={view === "featured"} icon={Star} label="Recommended" onClick={() => { setView("featured"); setMobileNavOpen(false); }} />
          <NavButton active={view === "favorites"} count={favorites.length} icon={Bookmark} label="Saved" onClick={() => { setView("favorites"); setMobileNavOpen(false); }} />
          <NavButton active={view === "recent"} count={recent.length} icon={Clock3} label="Recently used" onClick={() => { setView("recent"); setMobileNavOpen(false); }} />
        </nav>
        <div className="sidebar-label">Workflows</div>
        <nav aria-label="Prompt categories" className="category-nav">
          {categories.map((item) => {
            const Icon = categoryIcons[item] ?? FileText;
            return <button className={category === item ? "active" : ""} key={item} onClick={() => { setCategory(category === item ? "All" : item); setMobileNavOpen(false); }} type="button"><Icon size={16} /><span>{displayCategory(item)}</span><b>{promptLibrary.filter((prompt) => prompt.category === item).length}</b></button>;
          })}
        </nav>
        <div className="sidebar-note"><Sparkles size={16} /><div><strong>Built for project work</strong><p>Every prompt includes context fields, output structure, and review guardrails.</p></div></div>
      </aside>
      {mobileNavOpen && <button aria-label="Close navigation overlay" className="nav-scrim" onClick={() => setMobileNavOpen(false)} type="button" />}

      <main className="library-main">
        <section className="library-heading">
          <div><p className="section-kicker">Construction intelligence toolkit</p><h1>{viewTitle(view)}</h1><p>Start with the outcome. Add project context. Put the result through human review.</p></div>
          <div className="heading-stat"><span>Coverage</span><strong>6 workflows</strong><small>From pursuit through closeout</small></div>
        </section>

        <section aria-label="Prompt filters" className="filter-bar">
          <div className="filter-label"><Filter size={15} /><span>Filter</span></div>
          <label><span className="sr-only">Filter by role</span><select onChange={(event) => setRole(event.target.value as PromptRole | "All")} value={role}><option value="All">All roles</option>{roles.map((item) => <option key={item}>{item}</option>)}</select><ChevronDown size={14} /></label>
          <label><span className="sr-only">Filter by workflow</span><select onChange={(event) => setCategory(event.target.value as PromptCategory | "All")} value={category}><option value="All">All workflows</option>{categories.map((item) => <option key={item} value={item}>{displayCategory(item)}</option>)}</select><ChevronDown size={14} /></label>
          <label className="sort-control"><SlidersHorizontal size={14} /><span className="sr-only">Sort prompts</span><select onChange={(event) => setSort(event.target.value as SortMode)} value={sort}><option value="recommended">Recommended first</option><option value="title">Title A-Z</option><option value="quickest">Quickest setup</option></select><ChevronDown size={14} /></label>
          {(query || role !== "All" || category !== "All" || view !== "all") && <button className="clear-filters" onClick={clearFilters} type="button">Clear all</button>}
        </section>

        <div className="results-summary"><span><strong>{filtered.length}</strong> prompt{filtered.length === 1 ? "" : "s"}</span>{query && <span>matching “{query}”</span>}</div>

        {filtered.length > 0 ? <section aria-label="Construction prompts" className="prompt-grid">
          {filtered.map((prompt) => <PromptCard favorite={favorites.includes(prompt.id)} key={prompt.id} onFavorite={() => toggleFavorite(prompt.id)} onOpen={() => openPrompt(prompt)} prompt={prompt} />)}
        </section> : <section className="empty-state"><Search size={24} /><h2>No prompts found</h2><p>Try a broader term or remove a filter.</p><button onClick={clearFilters} type="button">Reset filters</button></section>}
      </main>

      {selected && <PromptInspector copied={copied} favorite={favorites.includes(selected.id)} onClose={() => { setSelected(null); setCopied(false); }} onCopied={() => { setCopied(true); window.setTimeout(() => setCopied(false), 1800); }} onFavorite={() => toggleFavorite(selected.id)} prompt={selected} />}
    </div>
  );
}

function NavButton({ active, count, icon: Icon, label, onClick }: { active: boolean; count?: number; icon: typeof FileText; label: string; onClick: () => void }) {
  return <button className={active ? "active" : ""} onClick={onClick} type="button"><Icon size={17} /><span>{label}</span>{count !== undefined && <b>{count}</b>}</button>;
}

function PromptCard({ favorite, onFavorite, onOpen, prompt }: { favorite: boolean; onFavorite: () => void; onOpen: () => void; prompt: ConstructionPrompt }) {
  return <article className="prompt-card">
    <button aria-label={`${favorite ? "Remove" : "Save"} ${prompt.title}`} className="save-button" onClick={onFavorite} type="button">{favorite ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}</button>
    <div className="card-meta"><span>{displayCategory(prompt.category)}</span>{prompt.featured && <b><Star size={11} /> Recommended</b>}</div>
    <h2><button onClick={onOpen} type="button">{prompt.title}</button></h2>
    <p>{prompt.outcome}</p>
    <div className="tag-row">{prompt.tags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}</div>
    <footer><span><Clock3 size={14} /> {prompt.estimatedMinutes} min setup</span><span>{prompt.role}</span><span aria-hidden="true" className="card-arrow"><ChevronRight size={17} /></span></footer>
  </article>;
}

function PromptInspector({ copied, favorite, onClose, onCopied, onFavorite, prompt }: { copied: boolean; favorite: boolean; onClose: () => void; onCopied: () => void; onFavorite: () => void; prompt: ConstructionPrompt }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [choiceValues, setChoiceValues] = useState<Record<string, string[]>>({});
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [tab, setTab] = useState<"setup" | "preview">("setup");
  const resolvedValue = (key: string) => [...(choiceValues[key] ?? []), values[key]?.trim()].filter(Boolean).join(", ");
  const completeCount = prompt.variables.filter((variable) => resolvedValue(variable.key)).length;
  const requiredComplete = requiredFields.every((key) => resolvedValue(key));
  const rendered = prompt.prompt.replace(/\{\{(.*?)\}\}/g, (_, key: string) => resolvedValue(key.trim()) || `[Optional: ${prompt.variables.find((item) => item.key === key.trim())?.label ?? key}]`);

  function toggleChoice(key: string, choice: string) {
    setChoiceValues((current) => {
      const selected = current[key] ?? [];
      return { ...current, [key]: selected.includes(choice) ? selected.filter((item) => item !== choice) : [...selected, choice] };
    });
  }

  async function copyPrompt() {
    await navigator.clipboard.writeText(rendered);
    onCopied();
  }

  return <div className="inspector-layer">
    <button aria-label="Close prompt" className="inspector-scrim" onClick={onClose} type="button" />
    <aside aria-label={`${prompt.title} prompt details`} aria-modal="true" className="prompt-inspector" role="dialog">
      <header><div><span>{displayCategory(prompt.category)}</span><strong>{prompt.role}</strong></div><div><button aria-label={favorite ? "Remove saved prompt" : "Save prompt"} className={favorite ? "saved" : ""} onClick={onFavorite} type="button">{favorite ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}</button><button aria-label="Close prompt" onClick={onClose} type="button"><X size={20} /></button></div></header>
      <section className="inspector-title"><h2>{prompt.title}</h2><p>{prompt.outcome}</p><div><span><Clock3 size={14} /> {prompt.estimatedMinutes} min setup</span><span>{prompt.variables.length} context fields</span></div></section>
      <div className="inspector-tabs" role="tablist"><button aria-selected={tab === "setup"} onClick={() => setTab("setup")} role="tab" type="button">Add context <span>{completeCount}/{prompt.variables.length}</span></button><button aria-selected={tab === "preview"} onClick={() => setTab("preview")} role="tab" type="button">Prompt preview</button></div>
      <div className="inspector-body">
        {tab === "setup" ? <form className="variable-form" onSubmit={(event) => { event.preventDefault(); setTab("preview"); }}>
          <div className="context-intro"><ShieldCheck size={18} /><p><strong>Your data stays in this browser.</strong> Replace sensitive project details before using external AI tools.</p></div>
          {prompt.variables.map((variable) => {
            const choices = choicesFor(variable);
            const isRequired = requiredFields.includes(variable.key);
            const labelId = `field-${prompt.id}-${variable.key}`;
            return <fieldset aria-labelledby={labelId} className="variable-field" key={variable.key}>
              <div className="field-heading"><strong id={labelId}>{variable.label}</strong><label className="required-toggle"><input checked={isRequired} onChange={() => setRequiredFields((current) => current.includes(variable.key) ? current.filter((key) => key !== variable.key) : [...current, variable.key])} type="checkbox" /><span>{isRequired ? "Required" : "Optional"}</span></label></div>
              {choices && <div aria-label={`${variable.label} choices`} className="choice-grid">{choices.map((choice) => <label key={choice}><input checked={(choiceValues[variable.key] ?? []).includes(choice)} onChange={() => toggleChoice(variable.key, choice)} type="checkbox" /><span>{choice}</span></label>)}</div>}
              <label className="freeform-field"><span className="sr-only">{choices ? `Other ${variable.label} details` : variable.label}</span><textarea aria-required={isRequired} onChange={(event) => setValues((current) => ({ ...current, [variable.key]: event.target.value }))} placeholder={choices ? "Add other details or project-specific context" : variable.placeholder} required={isRequired && !(choiceValues[variable.key]?.length)} rows={choices ? 2 : variable.placeholder.length > 65 ? 3 : 2} value={values[variable.key] ?? ""} /></label>
            </fieldset>;
          })}
          <button className="preview-button" type="submit">Review prompt <ChevronRight size={16} /></button>
        </form> : <div className="prompt-preview"><div className="preview-status"><span><Check size={14} /> {requiredComplete ? "Ready to use" : "Review missing context"}</span><button onClick={() => setTab("setup")} type="button">Edit context</button></div><pre>{rendered}</pre></div>}
        <section className="use-guidance"><h3>Use responsibly</h3>{prompt.tips.map((tip) => <p key={tip}><Check size={13} /> {tip}</p>)}</section>
      </div>
      <footer><div><span>{completeCount} of {prompt.variables.length} fields added{requiredFields.length ? ` / ${requiredFields.length} required` : " / all optional"}</span><div className="field-progress"><i style={{ width: `${prompt.variables.length ? (completeCount / prompt.variables.length) * 100 : 100}%` }} /></div></div><button aria-live="polite" className={copied ? "copied" : ""} disabled={!requiredComplete} onClick={copyPrompt} type="button">{copied ? <Check size={17} /> : <Copy size={17} />}{copied ? "Copied" : requiredComplete ? "Copy prompt" : "Complete required"}</button></footer>
    </aside>
  </div>;
}

function choicesFor(variable: PromptVariable): readonly string[] | null {
  const field = `${variable.key} ${variable.label}`.toLowerCase();
  if (field.includes("milestone")) return ["Permit", "Mobilization", "Structure complete", "Dry-in", "Permanent power", "Commissioning", "TCO", "Turnover"];
  if (field.includes("lead_item") || field.includes("long-lead")) return ["Switchgear", "Elevators", "Curtain wall", "AHUs", "Generators", "Structural steel", "Roofing", "Controls"];
  if (field.includes("design_release") || field.includes("design status") || field.includes("design milestone")) return ["Concept / SD", "Design development", "50% CDs", "90% CDs", "IFC", "Released for fabrication", "Selections unresolved"];
  if (field.includes("participant") || field.includes("leadership team")) return ["Owner", "Architect", "General contractor", "Engineer", "Key trades", "AHJ", "Commissioning agent"];
  if (field.includes("evidence") || field.includes("source record") || field.includes("available records") || field.includes("project records")) return ["Drawings", "Specifications", "RFIs", "Submittals", "Schedule", "Cost report", "Daily reports", "Meeting minutes"];
  if (field.includes("control") || field.includes("requirements")) return ["Permit", "AHA / JHA", "Inspection", "Testing", "Hold point", "Mockup", "Manufacturer guidance"];
  if (field.includes("open_items")) return ["RFI", "Submittal", "Procurement", "Permit", "Inspection", "Access", "Owner decision"];
  if (field.includes("estimate_stage")) return ["Conceptual", "Schematic design", "Design development", "GMP", "Bid", "Construction update"];
  if (field.includes("delivery model") || field.includes("delivery_terms")) return ["Design-bid-build", "Design-build", "CMAR", "GMP", "IPD", "Negotiated"];
  if (field.includes("tone") || field.includes("relationship context")) return ["Collaborative", "Neutral", "Direct", "Concerned", "Formal notice"];
  return null;
}

function viewTitle(view: LibraryView) {
  if (view === "featured") return "Recommended prompts";
  if (view === "favorites") return "Saved prompts";
  if (view === "recent") return "Recently used";
  return "What construction task are you working on?";
}

function displayCategory(category: PromptCategory) {
  return category.replace("Commercial/", "Commercial / ").replace("Safety/", "Safety / ");
}
